import { useEffect, useMemo, useState } from 'react';
import FMDashboardService, { IFMDashboardService } from '../../../services/FMDashboard.service';
import { getCurrentManagerAlias } from '../shared/fmUser';
import {
  AddEventForm,
  AddEventMasterData,
  ApprovalResponseItem,
  EventApprovalRequest,
  EventTypeOption,
  PendingCalendarItem,
} from '../../../contracts/FMPendingContracts';
import {
  GrmEventType,
  GrmPendingCalendarItem,
  NonProjectAssignmentRequest,
} from '../../../contracts/FMGrmContracts';
import {
  fmSampleAddMasterData,
  fmSampleDelegated,
  fmSampleEventTypes,
  fmSamplePending,
} from '../fmSampleData';
import {
  buildNonProjectAssignment,
  COMMON_TIMEZONES,
  RECURRENCE_OPTIONS,
  SHOWAS_OPTIONS,
} from '../FMAddEvent/fmAddEventPayload';

const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const toEventTypeOptions = (types: GrmEventType[]): EventTypeOption[] =>
  types
    .filter((t) => t.EventTypeName)
    .map((t) => ({
      EventTypeId: t.EventTypeId,
      EventTypeName: t.EventTypeName,
      EventColor: t.EventColor,
      IsCommentRequired: t.IsCommentRequired,
      IsBusy: t.IsBusy,
      IsSpecialtyRequired: t.IsSpecialtyRequired,
    }));

const toManagerAddEventTypes = (types: GrmEventType[]): EventTypeOption[] =>
  toEventTypeOptions(
    types.filter(
      (type) =>
        type.IsActive === true &&
        type.DisplayInEventList === true &&
        type.IsApplicableToManagers === true
    )
  );

/** Map a GRM pending item → the UI `PendingCalendarItem`, joining EventColorName from event types. */
const toPendingItem = (
  it: GrmPendingCalendarItem,
  colorByName: Record<string, string | undefined>
): PendingCalendarItem => ({
  Id: it.Id,
  ResourceId: it.ResourceId,
  ManagerAlias: it.ManagerAlias,
  FirstName: it.FirstName,
  LastName: it.LastName,
  EventTypeName: it.EventTypeName,
  EventColorName: it.EventColor ?? colorByName[(it.EventTypeName || '').toLowerCase()],
  Comments: it.Comments,
  StartDate: it.StartDate,
  EndDate: it.EndDate,
  EventStartDate: it.EventStartDate,
  EventEndDate: it.EventEndDate,
  TimeZone: it.TimeZone,
});

export type FMDataSource = 'live' | 'sample' | 'unavailable';

export interface FMPendingData {
  myItems: PendingCalendarItem[];
  delegatedItems: PendingCalendarItem[];
  eventTypes: EventTypeOption[];
  /** Master data for the Add Event form — live EventTypes/PrimaryTechnologies when available. */
  masterData: AddEventMasterData;
  /** Executes approve/reject against the live GRM PATCH endpoint. */
  manage: (requests: EventApprovalRequest[]) => Promise<ApprovalResponseItem[]>;
  /** Creates a non-project (OOF) event for a resource — POST CreateNonProjectRequestAndSchedules. */
  addEvent: (resourceAlias: string, form: AddEventForm) => Promise<void>;
  loading: boolean;
  source: FMDataSource;
  error?: string;
}

export interface UseFMPendingDataOptions {
  service?: IFMDashboardService;
  managerAlias?: string;
  /** StartDate lookback in months (parity default: 3). */
  lookbackMonths?: number;
  useSample?: boolean;
}

/**
 * Map the Add Event form → the GRM `CreateNonProjectRequestAndSchedules` body. The wire contract
 * takes the event type NAME (not the id) and the technology name alongside its id, so both are
 * resolved from master data here rather than in the presentational form.
 */
export const toNonProjectRequest = (
  resourceAlias: string,
  form: AddEventForm,
  masterData: AddEventMasterData
): NonProjectAssignmentRequest =>
  buildNonProjectAssignment(
    form,
    masterData.EventTypes,
    masterData.PrimaryTechnologies ?? [],
    resourceAlias
  );

const sampleManage = async (requests: EventApprovalRequest[]): Promise<ApprovalResponseItem[]> =>
  requests.map((r) => ({ RequestId: r.RequestId, IsSuccess: true }));

const sampleState = (
  manage: (r: EventApprovalRequest[]) => Promise<ApprovalResponseItem[]>,
  addEvent: (resourceAlias: string, form: AddEventForm) => Promise<void>,
  error?: string
): FMPendingData => ({
  myItems: fmSamplePending,
  delegatedItems: fmSampleDelegated,
  eventTypes: fmSampleEventTypes,
  masterData: fmSampleAddMasterData,
  manage,
  addEvent,
  loading: false,
  source: 'sample',
  error,
});

const unavailableManage = async (): Promise<ApprovalResponseItem[]> => {
  throw new Error('Pending approvals are unavailable.');
};

const unavailableAddEvent = async (): Promise<void> => {
  throw new Error('Add Event is unavailable.');
};

const emptyMasterData = (): AddEventMasterData => ({
  TimeZones: COMMON_TIMEZONES,
  EventTypes: [],
  ShowAs: SHOWAS_OPTIONS,
  RecurrenceData: RECURRENCE_OPTIONS,
  PrimaryTechnologies: [],
});

const unavailableState = (error?: string): FMPendingData => ({
  myItems: [],
  delegatedItems: [],
  eventTypes: [],
  masterData: emptyMasterData(),
  manage: unavailableManage,
  addEvent: unavailableAddEvent,
  loading: false,
  source: 'unavailable',
  error,
});

/**
 * Loads the FM pending OOF approvals from the live GRM API (GetMy + GetDelegatedPendingCalendarItems
 * + master data), and provides a live `manage` that PATCHes ApproveRejectCalendarItems plus a live
 * `addEvent` that POSTs CreateNonProjectRequestAndSchedules. Sample data is available only through
 * the explicit `useSample` component-test/local-preview option.
 */
export function useFMPendingData(options: UseFMPendingDataOptions = {}): FMPendingData {
  const liveManage = useMemo(
    () =>
      async (requests: EventApprovalRequest[]): Promise<ApprovalResponseItem[]> => {
        const service = options.service ?? FMDashboardService.getSingleton();
        const result = await service.approveRejectCalendarItems(
          requests.map((r) => ({
            RequestId: r.RequestId,
            IsApproved: r.IsApproved,
            DeclineComments: r.DeclineComments,
            ShouldApproveOrDeclineSeries: r.ShouldApproveOrDeclineSeries,
            StartDate: r.StartDate,
            EndDate: r.EndDate,
          }))
        );
        return result.map((x) => ({
          RequestId: x.RequestId,
          IsSuccess: x.IsSuccess,
          Message: x.Message,
        }));
      },
    [options.service]
  );

  // Bound to the master data actually in state so the event-type NAME the wire contract wants is
  // resolved from the SAME list the form rendered.
  const makeLiveAddEvent = useMemo(
    () => (masterData: AddEventMasterData) => async (
      resourceAlias: string,
      form: AddEventForm
    ): Promise<void> => {
      const service = options.service ?? FMDashboardService.getSingleton();
      await service.createNonProjectRequest(toNonProjectRequest(resourceAlias, form, masterData));
    },
    [options.service]
  );

  const sampleAddEvent = async (): Promise<void> => undefined;

  const [state, setState] = useState<FMPendingData>(() =>
    options.useSample
      ? sampleState(sampleManage, sampleAddEvent)
      : { ...unavailableState(), loading: true }
  );

  useEffect(() => {
    let cancelled = false;

    if (options.useSample) {
      setState(sampleState(sampleManage, sampleAddEvent));
      return;
    }

    const alias = options.managerAlias ?? getCurrentManagerAlias();
    if (!alias) {
      setState(
        unavailableState(
          'Unable to determine the current manager from the FXP user context.'
        )
      );
      return;
    }

    const service = options.service ?? FMDashboardService.getSingleton();
    const now = new Date();
    const start = new Date(now);
    start.setMonth(start.getMonth() - (options.lookbackMonths ?? 3));
    const request = { StartDate: ymd(start), EndDate: ymd(now), ManagerAlias: alias };

    setState({ ...unavailableState(), loading: true });

    (async () => {
      try {
        const [master, mine, delegated] = await Promise.all([
          service.getCalendarMasterData(),
          service.getMyPendingCalendarItems(request),
          service.getDelegatedPendingCalendarItems(request),
        ]);
        if (cancelled) {
          return;
        }
        const grmTypes = master?.DomainDataResult?.EventTypes ?? [];
        const colorByName: Record<string, string | undefined> = {};
        for (const t of grmTypes) {
          colorByName[(t.EventTypeName || '').toLowerCase()] = t.EventColor;
        }
        const myItems = (mine?.PendingCalendarItems ?? []).map((it) => toPendingItem(it, colorByName));
        const delegatedItems = (delegated?.PendingCalendarItems ?? []).map((it) =>
          toPendingItem(it, colorByName)
        );
        const eventTypes = toEventTypeOptions(grmTypes);
        const addEventTypes = toManagerAddEventTypes(grmTypes);
        // TimeZones / ShowAs / Recurrence are static UI option lists in the source app — only the
        // event types and technologies come from GetDomainData.
        const masterData: AddEventMasterData = {
          ...emptyMasterData(),
          EventTypes: addEventTypes,
          PrimaryTechnologies:
            master?.DomainDataResult?.PrimaryTechnologies ?? [],
        };
        setState({
          myItems,
          delegatedItems,
          eventTypes,
          masterData,
          manage: liveManage,
          addEvent: makeLiveAddEvent(masterData),
          loading: false,
          source: 'live',
        });
      } catch (e) {
        if (cancelled) {
          return;
        }
        setState(
          unavailableState(
            e instanceof Error ? e.message : 'Failed to load pending events'
          )
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    options.service,
    options.managerAlias,
    options.useSample,
    options.lookbackMonths,
    liveManage,
    makeLiveAddEvent,
  ]);

  return state;
}
